// Sélecteur rapide de dossier : épinglés + récents, partagés via un store Zustand
// (cohérence intra-document entre toutes les instances) et persistés en localStorage.
// La logique pure est dans lib/quickAccessStore.ts (testée). La synchro inter-onglets
// passe par l'événement `storage`.
import { useEffect } from 'react';
import { create } from 'zustand';
import {
  PINNED_KEY,
  RECENTS_KEY,
  addPin,
  pushRecent as pushRecentPure,
  readIds,
  removePin,
  writeIds,
} from '../lib/quickAccessStore';

interface QuickAccessState {
  pinnedIds: string[];
  recentIds: string[];
  togglePin: (id: string) => void;
  pushRecent: (id: string) => void;
  /** Recharge depuis localStorage (utilisé par la synchro inter-onglets). */
  reloadFromStorage: () => void;
}

const useStore = create<QuickAccessState>((set, get) => ({
  pinnedIds: readIds(PINNED_KEY),
  recentIds: readIds(RECENTS_KEY),
  togglePin: (id) => {
    const prev = get().pinnedIds;
    const next = prev.includes(id) ? removePin(prev, id) : addPin(prev, id);
    writeIds(PINNED_KEY, next);
    set({ pinnedIds: next });
  },
  pushRecent: (id) => {
    const next = pushRecentPure(get().recentIds, id);
    writeIds(RECENTS_KEY, next);
    set({ recentIds: next });
  },
  reloadFromStorage: () =>
    set({ pinnedIds: readIds(PINNED_KEY), recentIds: readIds(RECENTS_KEY) }),
}));

export interface DossierQuickAccess {
  pinnedIds: string[];
  recentIds: string[];
  isPinned: (id: string) => boolean;
  togglePin: (id: string) => void;
  pushRecent: (id: string) => void;
}

export function useDossierQuickAccess(): DossierQuickAccess {
  const pinnedIds = useStore((s) => s.pinnedIds);
  const recentIds = useStore((s) => s.recentIds);
  const togglePin = useStore((s) => s.togglePin);
  const pushRecent = useStore((s) => s.pushRecent);
  const reloadFromStorage = useStore((s) => s.reloadFromStorage);

  // Synchro inter-onglets : recharge si une autre instance/onglet modifie le storage.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === PINNED_KEY || e.key === RECENTS_KEY) reloadFromStorage();
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [reloadFromStorage]);

  const isPinned = (id: string) => pinnedIds.includes(id);

  return { pinnedIds, recentIds, isPinned, togglePin, pushRecent };
}
